// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  LocationReportType,
  getAssetLocationReport,
} from '@/report/api/get-asset-location-report';
import { useReportInterval } from '@/report/hooks/use-report-interval';
import { useQuery } from '@tanstack/react-query';

interface Options {
  type: LocationReportType;
  start?: string;
  end?: string;
  enabled?: boolean;
}

/**
 * A React hook that manages the state of a request for a location report of
 * assets for the current user's organization.
 *
 * @param options.type - The type of the report.
 * @param options.start - The start of the time period. If not provided, the
 * start date is 31 days before the current date.
 * @param options.end - The end of the time period. If not provided, the end
 * date is the day before the current date.
 * @param options.enabled - Whether the query should run. Defaults to true.
 */
export function useAssetLocationReport(options: Options) {
  const interval = useReportInterval(options);

  const query = useQuery({
    refetchOnWindowFocus: false,
    queryKey: ['report', options.type, interval.toString()],
    queryFn: () => {
      return getAssetLocationReport(options.type, {
        start: interval.start.toISODate(),
        end: interval.end.toISODate(),
      });
    },
    enabled: options.enabled ?? true,
  });

  return { interval, ...query };
}
